import { LEVELS } from '../levels';
import type { LevelDef } from '../levels/types';

const STORAGE_KEY = 'walkhome.campaign.v1';

type Saved = { unlocked: number; current: number; endings: string[] };

/**
 * Which nights she has reached. Saved in localStorage; if storage is blocked
 * (private window, previews) the campaign still works for the session.
 */
export class Campaign {
  unlocked = 1;
  current = 0;
  endings: string[] = [];

  constructor() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as Partial<Saved>;
        this.unlocked = clamp(saved.unlocked ?? 1, 1, LEVELS.length);
        this.current = clamp(saved.current ?? 0, 0, this.unlocked - 1);
        this.endings = Array.isArray(saved.endings) ? saved.endings : [];
      }
    } catch {
      // No storage: start fresh each session.
    }
    // Dev server: every night is selectable.
    if (import.meta.env.DEV) this.unlocked = LEVELS.length;
  }

  get level(): LevelDef {
    return LEVELS[this.current];
  }

  get total(): number {
    return LEVELS.length;
  }

  get hasNext(): boolean {
    return this.current + 1 < LEVELS.length;
  }

  select(index: number): boolean {
    if (index < 0 || index >= this.unlocked) return false;
    this.current = index;
    this.save();
    return true;
  }

  /** She made it home: unlock the next night. */
  complete(ending = 'home'): void {
    this.unlocked = Math.max(this.unlocked, Math.min(LEVELS.length, this.current + 2));
    if (!this.endings.includes(`${this.level.id}:${ending}`)) this.endings.push(`${this.level.id}:${ending}`);
    this.save();
  }

  next(): boolean {
    if (!this.hasNext || this.current + 1 >= this.unlocked) return false;
    this.current += 1;
    this.save();
    return true;
  }

  private save(): void {
    try {
      const data: Saved = { unlocked: this.unlocked, current: this.current, endings: this.endings };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore: progress just won't persist.
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, Math.floor(Number.isFinite(v) ? v : lo)));
}
